import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";

export interface ClientReportData {
  clientName: string;
  weekStart: string; // yyyy-MM-dd
  weekEnd: string;
  weekTotalHours: number;
  monthTotalHours: number;
  projectedMonthHours?: number | null;
  projects: { projectName: string; weekHours: number; monthHours: number }[];
  schedule: { title: string; assigneeName: string; start: string; end: string; hours: number; color: string }[];
  delivered: { title: string; assigneeName: string; hours: number; completedAt: string }[];
}

const ddmm = (iso: string) => {
  const p = iso.split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : iso;
};
const fmtH = (h: number) => {
  const t = Math.round((h || 0) * 60);
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};
const dayIndex = (iso: string) => {
  const p = iso.split("-").map(Number);
  return Math.floor(Date.UTC(p[0], p[1] - 1, p[2]) / 86400000);
};

const C = { navy: "#1b2559", gray: "#707eae", soft: "#a3aed0", track: "#f4f7fe", green: "#05cd99", brand: "#1466E3" };

const styles = StyleSheet.create({
  page: { fontFamily: "Helvetica", fontSize: 9, paddingTop: 26, paddingHorizontal: 28, paddingBottom: 30, color: C.navy },
  header: { backgroundColor: C.navy, borderRadius: 12, padding: 16, color: "#fff", marginBottom: 14 },
  brand: { fontSize: 8, letterSpacing: 1, color: "#8DDDFB" },
  hTitle: { fontSize: 16, fontFamily: "Helvetica-Bold", marginTop: 3 },
  hSub: { fontSize: 9, color: "#C4DBFD", marginTop: 2 },
  kpiRow: { flexDirection: "row", marginBottom: 16 },
  kpi: { flex: 1, backgroundColor: C.track, borderRadius: 10, padding: 12, marginRight: 8 },
  kpiLabel: { fontSize: 8, color: C.gray },
  kpiValue: { fontSize: 18, fontFamily: "Helvetica-Bold", marginTop: 3 },
  section: { fontSize: 12, fontFamily: "Helvetica-Bold", marginTop: 6, marginBottom: 6 },
  sectionSub: { fontSize: 8, color: C.soft, marginBottom: 6 },
  thRow: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#eef0f4", paddingBottom: 4, marginBottom: 2 },
  th: { fontSize: 8, color: C.gray, fontFamily: "Helvetica-Bold" },
  tr: { flexDirection: "row", borderTopWidth: 0.5, borderTopColor: "#eef0f4", paddingVertical: 4 },
  cellName: { flex: 1, fontSize: 9, fontFamily: "Helvetica-Bold" },
  cellNum: { width: 60, textAlign: "right", fontSize: 9 },
  ganttRow: { flexDirection: "row", alignItems: "center", paddingVertical: 4 },
  ganttName: { width: "44%", paddingRight: 8 },
  delivered: { backgroundColor: "#ecfdf5", borderWidth: 1, borderColor: "#a7f3d0", borderRadius: 8, padding: 8, marginBottom: 6 },
});

const ClientReportPdf: React.FC<{ data: ClientReportData }> = ({ data }) => {
  const timelineW = 539 - Math.round(539 * 0.44) - 6; // largura aproximada da faixa do gantt
  let min = 0;
  let max = 1;
  if (data.schedule.length) {
    const starts = data.schedule.map((s) => dayIndex(s.start));
    const ends = data.schedule.map((s) => dayIndex(s.end));
    min = Math.min(...starts);
    max = Math.max(...ends);
  }
  const totalDays = Math.max(1, max - min + 1);

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header}>
          <Text style={styles.brand}>DATAVALUE · NINA</Text>
          <Text style={styles.hTitle}>{data.clientName}</Text>
          <Text style={styles.hSub}>
            Report semanal · {ddmm(data.weekStart)} a {ddmm(data.weekEnd)}
          </Text>
        </View>

        {/* KPIs */}
        <View style={styles.kpiRow}>
          <View style={styles.kpi}>
            <Text style={styles.kpiLabel}>Horas na semana</Text>
            <Text style={styles.kpiValue}>{fmtH(data.weekTotalHours)}</Text>
          </View>
          <View style={styles.kpi}>
            <Text style={styles.kpiLabel}>Mês até agora</Text>
            <Text style={[styles.kpiValue, { color: C.green }]}>{fmtH(data.monthTotalHours)}</Text>
          </View>
          {data.projectedMonthHours != null ? (
            <View style={[styles.kpi, { backgroundColor: "#E7F0FE", marginRight: 0 }]}>
              <Text style={styles.kpiLabel}>Previsão do mês</Text>
              <Text style={[styles.kpiValue, { color: C.brand }]}>{fmtH(data.projectedMonthHours)}</Text>
            </View>
          ) : (
            <View style={[styles.kpi, { marginRight: 0, backgroundColor: "#fff" }]} />
          )}
        </View>

        {/* Horas por projeto */}
        {data.projects.length > 0 && (
          <>
            <Text style={styles.section}>Horas por projeto</Text>
            <View style={styles.thRow}>
              <Text style={[styles.th, { flex: 1 }]}>Projeto</Text>
              <Text style={[styles.th, styles.cellNum]}>Semana</Text>
              <Text style={[styles.th, styles.cellNum]}>Mês</Text>
            </View>
            {data.projects.map((p, i) => (
              <View key={i} style={styles.tr}>
                <Text style={styles.cellName}>{p.projectName}</Text>
                <Text style={styles.cellNum}>{fmtH(p.weekHours)}</Text>
                <Text style={[styles.cellNum, { color: C.green }]}>{fmtH(p.monthHours)}</Text>
              </View>
            ))}
          </>
        )}

        {/* Cronograma */}
        {data.schedule.length > 0 && (
          <>
            <Text style={[styles.section, { marginTop: 16 }]}>Cronograma das tarefas</Text>
            <Text style={styles.sectionSub}>Entregas planejadas a partir desta semana</Text>
            {data.schedule.map((s, i) => {
              const offset = dayIndex(s.start) - min;
              const dur = dayIndex(s.end) - dayIndex(s.start) + 1;
              const left = (offset / totalDays) * timelineW;
              const width = Math.max(10, (dur / totalDays) * timelineW);
              return (
                <View key={i} style={styles.ganttRow} wrap={false}>
                  <View style={styles.ganttName}>
                    <Text style={{ fontSize: 9, fontFamily: "Helvetica-Bold" }}>{s.title}</Text>
                    <Text style={{ fontSize: 7.5, color: C.gray }}>
                      {s.assigneeName}
                      {s.hours > 0 ? ` · ${s.hours}h` : ""}
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={{ height: 16, backgroundColor: C.track, borderRadius: 5, position: "relative" }}>
                      <View
                        style={{
                          position: "absolute", left, top: 0, width, height: 16,
                          backgroundColor: s.color, borderRadius: 5, justifyContent: "center", paddingHorizontal: 4,
                        }}
                      >
                        {width > 34 ? (
                          <Text style={{ fontSize: 6.5, color: "#fff" }}>
                            {ddmm(s.start)}–{ddmm(s.end)}
                          </Text>
                        ) : null}
                      </View>
                    </View>
                  </View>
                </View>
              );
            })}
          </>
        )}

        {/* Entregas */}
        <Text style={[styles.section, { marginTop: 16 }]}>Entregue nesta semana ({data.delivered.length})</Text>
        {data.delivered.length === 0 ? (
          <View style={{ backgroundColor: C.track, borderRadius: 8, padding: 10 }}>
            <Text style={{ fontSize: 9, color: C.gray }}>Nenhuma tarefa concluída nesta semana.</Text>
          </View>
        ) : (
          data.delivered.map((d, i) => (
            <View key={i} style={styles.delivered} wrap={false}>
              <Text style={{ fontSize: 9, fontFamily: "Helvetica-Bold", color: C.navy }}>✓ {d.title}</Text>
              <Text style={{ fontSize: 7.5, color: "#059669", marginTop: 2 }}>
                {d.assigneeName}
                {d.hours > 0 ? ` · ${d.hours}h` : ""} · concluída em {ddmm(d.completedAt)}
              </Text>
            </View>
          ))
        )}
      </Page>
    </Document>
  );
};

export default ClientReportPdf;
